import React from 'react';
import LegalPage, { Section, List, Link } from '@/components/Pages/LegalPage';
import Paragraph from '@/components/Typography/Paragraph';
import Text from '@/components/Typography/Text';
import MetaHead from '@/components/MetaHead';

const RefundPolicyPage = () => {
  return (
    <LegalPage
      title="Refund Policy"
      lastUpdated="September 28, 2026"
    >
      <MetaHead 
        title="Refund Policy"
        description="Gab AI refund policy for subscriptions, Gab AI Business seats, credit packs, and Auto Top-Up, including cancellation, billing errors, and statutory rights."
        canonical="/refund-policy"
        keywords="Gab AI refund, refund policy, subscription refund, credit refund, cancellation policy"
      />

      <Section title="Subscriptions">
        <Section title="Freedom, Fairness, and Transparency">
          <Paragraph>
            This policy applies to all Gab AI subscription tiers, including Gab AI Plus and Gab AI Plus Premium
            (each a "Subscription"). When you subscribe, you're supporting an independent technology platform built
            outside the control of Big Tech. We aim to be transparent and fair in all our dealings — that includes
            how we handle payments, billing, and refunds.
          </Paragraph>
          <Paragraph>
            By subscribing, you authorize Gab AI — through our secure payment processor — to charge your chosen payment method (credit card, debit card, etc.) for the plan you select, including any applicable taxes. You're responsible for keeping your billing information current so that your access to our services remains uninterrupted.
          </Paragraph>
        </Section>
      </Section>

      <Section title="Credits">
        <Section title="Credit Purchases and Non-Refundability">
          <Paragraph>
            Gab AI credits provide prepaid access to eligible AI models and tools, including Work. Credit purchases include one-time packs, Quick Top-Up, and separately authorized Auto Top-Up purchases governed by our <Link href="/terms-of-sale">Terms of Sale</Link>.
          </Paragraph>
          <Paragraph>
            <Text weight='extraBold'>Credit purchases are generally final and non-refundable.</Text> Credits cannot be transferred or exchanged for cash, except where required by law. Purchased credits do not expire, subject to account restrictions or termination under the Terms of Service and applicable law. The billing-error provisions and mandatory rights below apply to all credit purchases.
          </Paragraph>
          <Paragraph>
            Disabling Auto Top-Up stops new automatic purchases after the setting is saved; a payment already submitted may still complete. Disabling it or canceling a subscription does not by itself expire purchased credits or refund completed purchases. You retain your lawful rights to dispute unauthorized or incorrect charges.
          </Paragraph>
        </Section>
      </Section>

      <Section title="Gab AI Business">
        <Paragraph>
          Gab AI Business seat subscriptions and company credit packs follow this policy and the Business section of
          our <Link href="/terms-of-sale">Terms of Sale</Link>. Business Subscriptions are billed monthly to a saved
          card. An owner can cancel from Settings → Business → Billing on the website; cancellation stops the next
          renewal, and the workspace keeps its paid seats until the end of the paid period.
        </Paragraph>
        <Paragraph>
          Seats added or upgraded during a period are charged pro rata after you confirm the amount. Seat reductions
          and downgrades take effect at renewal, so we generally do not refund unused seats or unused time, and
          removing a member does not reduce the bill. Company credit purchases are generally final. The exception
          below for billing errors applies to Business, and you can also contact ai@gab.com about a company charge
          that is unauthorized, duplicate, or incorrect, or credits that are missing. A personal Apple or Google
          subscription is separate; refunds for it are handled by that store.
        </Paragraph>
        <Paragraph>
          If a Business payment is refunded, voided, returned, or disputed with the payment processor, we pause the
          workspace while we review it. New requests and seat changes are unavailable during the review, and existing
          chats and files remain visible. Contact ai@gab.com to resolve it. We do not adjust credits or seats
          automatically for a partial refund; we review it with you.
        </Paragraph>
      </Section>

      <Section title="Why We Don't Offer Refunds">
        <Paragraph>
          Subscription and credit fees support access to computing, models, tools, and infrastructure. Authorized Work processing may consume credits even if a task is stopped or its output requires revision; payment does not guarantee a particular result. Express billing commitments and mandatory consumer rights remain applicable.
        </Paragraph>
        <Paragraph>
          <Text weight='extraBold'>That's why payments for Subscriptions and credits are non-refundable and non-transferable</Text> (except as provided below or required by law). Completing your purchase confirms your understanding and acceptance of this policy.
        </Paragraph>
        <Paragraph>
          This is clearly shown during checkout, because we believe in honest terms — no fine print, no surprises.
        </Paragraph>
      </Section>

      <Section title="Billing and Renewal">
        <Paragraph>
          Subscriptions renew automatically unless canceled, on the monthly or annual billing cycle disclosed at purchase. Renewal charges use your saved payment method, subject to the price-change notice requirements in the Terms of Sale. Auto Top-Up is a separate, optional authorization for usage-triggered credit purchases; it is not a subscription renewal.
        </Paragraph>
        <Paragraph>
          Your Subscription stays active until you cancel it yourself or Gab AI terminates it according to our <Link href="/terms-of-service">Terms of Service</Link>.
        </Paragraph>

        <Section title="How to Cancel">
          <Paragraph>
            You can cancel at any time from your account dashboard. Cancellation takes effect at the end of your current billing period. We generally do not offer partial or prorated refunds for unused time, except where required by law. You can separately disable Auto Top-Up in your Gab AI account settings.
          </Paragraph>
        </Section>
      </Section>

      <Section title="Limited Exceptions">
        <Paragraph>
          The following exceptions apply to subscriptions and credit purchases, including Auto Top-Up:
        </Paragraph>
        <Paragraph>
          <Text weight='medium'>Billing Errors:</Text> Contact ai@gab.com if you believe a charge is unauthorized, duplicate, or incorrect, or purchased credits are missing. We will investigate and correct confirmed billing errors, including issuing refunds where appropriate or legally required.
        </Paragraph>
        <Paragraph>
          We generally do not issue refunds for unused time, unused credits, or changes of mind. Nothing in this policy overrides mandatory cancellation, withdrawal, refund, payment-dispute, or other consumer rights. Purchases through an app store or other external checkout may also be subject to that provider's applicable refund process.
        </Paragraph>
      </Section>

      <Section title="Disputes and Chargebacks">
        <Paragraph>
          We're a small, independent company — not a faceless corporation. If something goes wrong, talk to us first. Most issues can be resolved quickly and fairly by contacting support.
        </Paragraph>
        <Paragraph>
          Contacting support can help us resolve an issue, but it is not a condition of exercising any lawful payment-dispute or chargeback right. Where permitted by law, we may provide payment processors with relevant records when responding to a dispute, including:
        </Paragraph>
        <List items={[
          'Available records of purchase confirmations and any separate Auto Top-Up authorization',
          'Logs confirming your account activity',
          'Relevant payment, credit-allocation, and service-usage records'
        ]} />
        <Paragraph>
          We value honest communication and mutual respect. We'd rather work things out directly with you — person to person.
        </Paragraph>
      </Section>

      <Section title="Policy Updates">
        <Paragraph>
          We may update this Refund Policy from time to time to reflect new services, legal requirements, or improvements to our systems. When changes occur, we'll notify you through email or platform announcements.
        </Paragraph>
      </Section>

      <Section title="Final Word">
        <Paragraph>
          Gab AI exists to give you freedom — not run you through the same corporate nonsense the rest of the internet does. That freedom also comes with personal responsibility. By purchasing a Subscription or credits, you're helping us build and maintain a sovereign AI platform that serves real people, not algorithms.
        </Paragraph>
        <Paragraph>
          If you have questions, contact our team — we're here to help and always strive to handle matters fairly and directly.
        </Paragraph>
      </Section>
    </LegalPage>
  );
};

export default React.memo(RefundPolicyPage);
